import React, { useEffect, useState } from 'react';
import { Row, Col, Spinner } from 'react-bootstrap';
import LastMonthCostChart from './charts/LastMonthCostChart';
import RealTimeUsageChart from './charts/RealTimeUsageChart';
import TodayUsageChart from './charts/TodayUsageChart';
import UsageChart from './charts/UsageChart';
import BillingSummary from './charts/BillingSummary';
import BillingPanel from './panels/BillingPanel';
import { ServiceItemNumber } from './utils/serviceItem';
import { fetchBillingData, fetchBillingDetail } from 'hooks/CallApi';
import { isDemoMode } from 'data/demoMockData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import {
    PageContainer,
    HeaderSection,
    TitleWrapper,
    IconWrapper,
    PageTitle,
    PageSubtitle,
    StyledCard,
    SectionTitle,
    SegmentedControl,
    LoadingContainer,
    ErrorAlert
} from './styles/BillingStyledComponents';

const MemoizedLastMonthCostChart = React.memo(LastMonthCostChart);
const MemoizedRealTimeUsageChart = React.memo(RealTimeUsageChart);
const MemoizedTodayUsageChart = React.memo(TodayUsageChart);
const MemoizedUsageChart = React.memo(UsageChart);
const MemoizedBillingSummary = React.memo(BillingSummary);
const MemoizedBillingPanel = React.memo(BillingPanel);

const SERVICE_TYPES = [
    { key: 'sdk', label: 'SDK' },
    { key: 'cf', label: 'CF' },
    { key: 'api', label: 'API' }
];

const BillingDashboard = () => {
    const [billingData, setBillingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedYearMonth, setSelectedYearMonth] = useState(null);
    const [detailsMap, setDetailsMap] = useState({});
    const [serviceType, setServiceType] = useState('sdk');

    const defaultServiceData = {
        SDK: [
            { name: '예/적금', amount: 0 },
            { name: '입출금', amount: 0 },
            { name: '가입자 생성/조회', amount: 0 },
            { name: '대출', amount: 0 }
        ],
        CF: [
            { name: 'Cloud Function 호출', amount: 0 },
            { name: '배포 관리', amount: 0 }
        ],
        API: [
            { name: 'API Gateway', amount: 0 },
            { name: 'API 요청 처리', amount: 0 }
        ]
    };

    const defaultBillingData = {
        lastMonthCost: 0,
        thisMonthCost: 0,
        forecastCost: 0,
        billingDetails: {
            totalAmount: 0,
            finalAmount: 0,
            discount: 0,
            items: { CF: [], API: [], SDK: [] }
        },
        monthlyData: [
            {
                month: 4, year: 2025, totalAmount: 0, finalAmount: 0, discount: 0,
                items: { CF: [], API: [], SDK: [] },
                totalRequests: 0, pricingPolicy: { name: "", tiers: [] }
            },
            {
                month: 5, year: 2025, totalAmount: 0, finalAmount: 0, discount: 0,
                items: { CF: [], API: [], SDK: [] },
                totalRequests: 0, pricingPolicy: { name: "", tiers: [] }
            }
        ],
        lastDailyCosts: Array.from({ length: 30 }, (_, index) => ({
            date: `2025-04-${String(index + 1).padStart(2, '0')}`,
            dailyFee: 0
        }))
    };

    const zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // 데모 모드용 mock 정책 트리. 실제 백엔드 PricingPolicy DTO 직렬화 형태와 동일한 키 사용.
    // 실 서비스에서는 detail.pricingPolicyTree로 같은 형태가 들어옴.
    const DEMO_POLICY_TREE = {
        pricingPolicyUuid: 'demo-policy-uuid-v3',
        originalPricingPolicyUuid: 'demo-policy-original-uuid',
        pricingPolicyName: 'SaaS Standard',
        version: 3,
        description: 'Demo policy (v3)',
        compensationAmount: '500000',
        serviceGroupList: [
            {
                uuid: 'demo-sdk-group',
                serviceItemNumber: 1,
                serviceItemName: 'SDK',
                compensationAmount: '0',
                servicePriceList: [
                    {
                        uuid: 'demo-sdk-agent',
                        serviceItemNumber: 2,
                        serviceItemName: 'SDK Agent',
                        price: 50000,
                        paymentUnit: 'month',
                        compensationAmount: '2160000',
                        // schema_manager init_data.sql의 SDK Agent 구간 정의 그대로 차용 (5단계)
                        // 1~30: 0% / 31~60: 10% / 61~100: 25% / 101~150: 40% / 151~99999: 60%
                        serviceTierPriceList: [
                            { startAmount: 1,   endAmount: 30,    discountAmount: 0,  discountUnit: 'percent' },
                            { startAmount: 31,  endAmount: 60,    discountAmount: 10, discountUnit: 'percent' },
                            { startAmount: 61,  endAmount: 100,   discountAmount: 25, discountUnit: 'percent' },
                            { startAmount: 101, endAmount: 150,   discountAmount: 40, discountUnit: 'percent' },
                            { startAmount: 151, endAmount: 99999, discountAmount: 60, discountUnit: 'percent' }
                        ]
                    },
                    {
                        uuid: 'demo-ps',
                        serviceItemNumber: 3,
                        serviceItemName: '정책서버',
                        price: 300000,
                        paymentUnit: 'month',
                        compensationAmount: '300000',
                        serviceTierPriceList: []
                    }
                ]
            },
            {
                uuid: 'demo-cf-group',
                serviceItemNumber: 4,
                serviceItemName: 'CF',
                compensationAmount: '0',
                servicePriceList: []
            },
            {
                uuid: 'demo-api-group',
                serviceItemNumber: 5,
                serviceItemName: 'API',
                compensationAmount: '0',
                servicePriceList: []
            }
        ]
    };

    const generateDemoBillingData = () => {
        const now = new Date();
        const curYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const prevYM = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}`;
        const daysInLastMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();

        const lastDailyCosts = Array.from({ length: daysInLastMonth }, (_, i) => ({
            date: `${prevYM}-${String(i + 1).padStart(2, '0')}`,
            dailyFee: Math.floor(Math.random() * 5000) + 10000,
            agentCount: Math.floor(Math.random() * 3) + 5,
            avgAgentCount: (Math.random() * 2 + 4).toFixed(2)
        }));
        const lastMonthCost = lastDailyCosts.reduce((s, d) => s + d.dailyFee, 0);
        const thisMonthCost = Math.floor(lastMonthCost * 0.6);
        const forecastCost = Math.floor(thisMonthCost / Math.max(now.getDate(), 1) * daysInLastMonth);
        const yearMonthOptions = [curYM, prevYM];

        const makeBillingDetail = (ym) => {
            const isCurrentMonth = ym === curYM;
            const serverCost = 600000;
            const basePricePerAgent = 50000;

            // Deterministic 데모 데이터로 구간별 요금 검증 가능하게 함.
            // SDK Agent 구간 정의 (DEMO_POLICY_TREE.serviceTierPriceList와 일치):
            //   1~30   :  0% 할인
            //   31~60  : 10% 할인
            //   61~100 : 25% 할인
            //   101~150: 40% 할인
            //   151~∞  : 60% 할인
            //
            // 이번달:    평균 45명  → tier 1 + 2 활성
            // 지난달:    평균 175명 → tier 1~5 모두 활성 (전 구간 노출)
            const avgAgentCount = isCurrentMonth ? 45 : 175;
            const maxAgentCount = isCurrentMonth ? 50 : 190;

            // 구간 경계 정의
            const tierBoundaries = [
                { from: 1,   to: 30,    discountRate: 0 },
                { from: 31,  to: 60,    discountRate: 10 },
                { from: 61,  to: 100,   discountRate: 25 },
                { from: 101, to: 150,   discountRate: 40 },
                { from: 151, to: 99999, discountRate: 60 }
            ];

            // 구간별 사용 분배
            const tierBreakdown = tierBoundaries.map((b, idx) => {
                const tierMax = b.to - b.from + 1;
                const used = Math.max(0, Math.min(avgAgentCount - (b.from - 1), tierMax));
                const grossFee = Math.round(used * basePricePerAgent);
                const tierDiscount = Math.round(grossFee * (b.discountRate / 100));
                const netFee = grossFee - tierDiscount;
                return {
                    tier_level: `Tier ${idx + 1}`,
                    agent_count_from: b.from,
                    agent_count_to: b.to,
                    agents_in_tier: used.toFixed(4),
                    price_per_agent: basePricePerAgent,
                    monthly_fee_for_tier: grossFee,
                    netFee,
                    discount_rate: b.discountRate,
                    discount_type: 'percent',
                    tier_discount: tierDiscount,
                    used
                };
            });

            const totalTierDiscount = tierBreakdown.reduce((s, t) => s + t.tier_discount, 0);

            const tierDiscountInfo = JSON.stringify({
                // 사용량이 0인 tier는 노출하지 않음
                applied_tiers: tierBreakdown
                    .filter(t => t.used > 0)
                    .map(t => ({
                        tier_level: t.tier_level,
                        agent_count_from: t.agent_count_from,
                        agent_count_to: t.agent_count_to,
                        agents_in_tier: t.agents_in_tier,
                        price_per_agent: t.price_per_agent,
                        monthly_fee_for_tier: t.monthly_fee_for_tier,
                        discount_rate: t.discount_rate,
                        discount_type: t.discount_type,
                        tier_discount: t.tier_discount
                    }))
            });

            const sdkAgentTotal = tierBreakdown.reduce((s, t) => s + t.netFee, 0);
            const sdkFee = sdkAgentTotal + serverCost;
            const grandTotalFee = sdkFee;
            const discountFee = totalTierDiscount;
            const finalFee = grandTotalFee - discountFee;

            // 보전금액 데모: DEMO_POLICY_TREE의 항목 보전과 일치하도록 설정.
            //   - 정책 보전: ₩500,000 (사용량 < 정책 보전이면 적용, 그렇지 않으면 미적용)
            //   - 그룹 보전: ₩0 (미적용)
            //   - SDK Agent 항목 보전: 사용량의 1.2배 → 적용 트리거 (사용요금에 strikethrough)
            //   - 정책서버 항목 보전: 사용량의 0.5배 → 미적용 (보전기준에 strikethrough)
            const policyComp = 500000;
            const groupComp = 0;
            const sdkAgentComp = Math.round(sdkAgentTotal * 1.2);
            const psComp = Math.round(serverCost * 0.5);
            const compTotal = policyComp + groupComp + sdkAgentComp + psComp;
            const usageBasedFeeDemo = sdkAgentTotal + serverCost;
            const compensationApplied = !isCurrentMonth;

            // 항목별 fee 분리 (백엔드 응답 컨벤션과 일치, stringified int 키)
            const sdkKey = String(ServiceItemNumber.SDK_AGENT);
            const psKey = String(ServiceItemNumber.POLICY_SERVER);
            const itemFees = {
                [sdkKey]: sdkAgentTotal,
                [psKey]: serverCost,
            };
            // 보전 적용 시 SDK Agent에 보전기준이 더해짐 (데모: 사용량의 1.2배가 보전기준)
            const adjustedItemFees = compensationApplied
                ? { [sdkKey]: sdkAgentComp, [psKey]: serverCost }
                : { ...itemFees };

            // 데모: raw 수량 (소수 보존용)
            const rawItemCounts = {
                [sdkKey]: avgAgentCount,
                [psKey]: 2, // 데모 정책서버는 정수
            };

            return {
                yearMonth: ym, totalMonthlyFee: grandTotalFee, discountFee, finalFee,
                itemFees, adjustedItemFees, rawItemCounts,
                pricingPolicyName: DEMO_POLICY_TREE.pricingPolicyName,
                pricingPolicyVersion: DEMO_POLICY_TREE.version,
                pricingPolicyUuid: DEMO_POLICY_TREE.pricingPolicyUuid,
                pricingPolicyOriginalUuid: DEMO_POLICY_TREE.originalPricingPolicyUuid,
                pricingPolicyTree: DEMO_POLICY_TREE,
                avgAgentCount: avgAgentCount.toFixed(2), maxAgentCount, basePricePerAgent,
                tierDiscountInfo, policyServerCnt: 2,
                option: JSON.stringify({ policy: { price: 300000 } }),
                finalAmount: !isCurrentMonth,
                finalized_at: !isCurrentMonth ? `${ym}-${daysInLastMonth}T23:59:00` : null,
                compensationApplied,
                compensationAmount: compensationApplied ? compTotal : 0,
                usageBasedFee: compensationApplied ? usageBasedFeeDemo : null,
                compensationInfo: compensationApplied ? {
                    policy: policyComp,
                    groups: [
                        { uuid: 'demo-sdk-group', name: 'SDK', groupItemNumber: 1, amount: groupComp }
                    ],
                    items: [
                        { uuid: 'demo-sdk-agent', name: 'SDK Agent', itemNumber: 2, groupUuid: 'demo-sdk-group', amount: sdkAgentComp },
                        { uuid: 'demo-ps', name: '정책서버', itemNumber: 3, groupUuid: 'demo-sdk-group', amount: psComp }
                    ],
                    total: compTotal
                } : null,
                // 신규 BillingPanel은 pricingPolicyTree 기반으로 렌더하므로 items는 더 이상 사용 안 함.
                // 호환성을 위해 SDK만 남겨둠 (legacy fallback).
                items: {
                    SDK: [
                        { name: 'SDK Agent', amount: sdkAgentTotal },
                        { name: '정책서버', amount: serverCost }
                    ]
                }
            };
        };

        return {
            data: {
                lastMonthCost, thisMonthCost, forecastCost,
                lastDailyCosts, yearMonthOptions,
                billingDetailsList: yearMonthOptions.map(makeBillingDetail)
            }
        };
    };

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = isDemoMode() ? generateDemoBillingData() : await fetchBillingData(zoneId);
                const data = res?.data;
                if (data && Array.isArray(data.yearMonthOptions) && data.yearMonthOptions.length > 0) {
                    setBillingData(data);
                    setSelectedYearMonth(data.yearMonthOptions[0]);
                    const map = {};
                    (data.billingDetailsList || []).forEach(d => { map[d.yearMonth] = d; });
                    setDetailsMap(map);
                } else {
                    setBillingData(defaultBillingData);
                    setSelectedYearMonth('2025-04');
                }
            } catch (e) {
                setError('데이터를 불러오는 중 오류가 발생했습니다.');
                setBillingData(defaultBillingData);
                setSelectedYearMonth('2025-04');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const handleMonthChange = async (yearMonth) => {
        setSelectedYearMonth(yearMonth);
        if (!detailsMap[yearMonth]) {
            try {
                const detail = await fetchBillingDetail(yearMonth, zoneId);
                setDetailsMap(prev => ({ ...prev, [yearMonth]: detail }));
            } catch (e) {
                setError('상세 요금 정보를 불러오지 못했습니다.');
            }
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <LoadingContainer>
                    <Spinner animation="border" variant="primary" size="sm" />
                    <span>청구 데이터를 불러오는 중...</span>
                </LoadingContainer>
            </PageContainer>
        );
    }

    const effectiveBillingData = billingData || defaultBillingData;
    const selectedDetail = detailsMap[selectedYearMonth] ||
        (effectiveBillingData.billingDetailsList || []).find(d => d.yearMonth === selectedYearMonth) ||
        null;

    const today = new Date();
    const currentDay = today.getDate();
    const lastMonthSamePeriodCost = (effectiveBillingData.lastDailyCosts || [])
        .slice(0, currentDay - 1)
        .reduce((sum, d) => sum + (d.dailyFee || 0), 0);

    const monthOptions = effectiveBillingData.yearMonthOptions || [];
    const lastMonthCost = effectiveBillingData?.lastMonthCost || 0;
    const thisMonthCost = effectiveBillingData?.thisMonthCost || 0;
    const forecastCost = effectiveBillingData?.forecastCost || 0;

    return (
        <PageContainer>
            <HeaderSection>
                <TitleWrapper>
                    <IconWrapper>
                        <FontAwesomeIcon icon={faFileInvoiceDollar} />
                    </IconWrapper>
                    <div>
                        <PageTitle>청구 내역</PageTitle>
                        <PageSubtitle>서비스 이용 요금 및 청구 현황을 확인합니다.</PageSubtitle>
                    </div>
                </TitleWrapper>
            </HeaderSection>

            {error && (
                <ErrorAlert>
                    <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ opacity: 0.6 }} />
                    {error}
                </ErrorAlert>
            )}

            <Row className="g-3 mb-4">
                <Col md={4}>
                    <StyledCard className="h-100">
                        <MemoizedLastMonthCostChart
                            lastMonthCost={lastMonthCost}
                            dailyCosts={effectiveBillingData.lastDailyCosts}
                        />
                    </StyledCard>
                </Col>
                <Col md={4}>
                    <StyledCard className="h-100">
                        <MemoizedBillingSummary
                            title="이번달 비용"
                            amount={thisMonthCost}
                            comparisonAmount={lastMonthSamePeriodCost}
                            progressPercentage={(today.getDate() / new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()) * 100}
                            showProgress={true}
                            isSamePeriodComparison={true}
                            decimalPlaces={2}
                            roundingMethod="round"
                        />
                    </StyledCard>
                </Col>
                <Col md={4}>
                    <StyledCard className="h-100">
                        <MemoizedBillingSummary
                            title="예상 금액"
                            amount={forecastCost}
                            comparisonAmount={lastMonthCost}
                            showProgress={false}
                            isForecast={true}
                            decimalPlaces={2}
                            roundingMethod="round"
                        />
                    </StyledCard>
                </Col>
            </Row>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '1.25rem' }}>
                <span style={{ fontWeight: 600, color: '#475569', fontSize: '0.9rem' }}>서비스 선택</span>
                <SegmentedControl>
                    {SERVICE_TYPES.map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            className={`segment-btn ${serviceType === key ? 'active' : ''}`}
                            onClick={() => setServiceType(key)}
                        >
                            {label}
                        </button>
                    ))}
                </SegmentedControl>
            </div>

            <Row className="g-3 mb-4">
                <Col md={4}>
                    <StyledCard className="h-100">
                        <MemoizedRealTimeUsageChart serviceType={serviceType} />
                    </StyledCard>
                </Col>
                <Col md={8}>
                    <Row className="g-3">
                        <Col md={12}>
                            <StyledCard>
                                <MemoizedTodayUsageChart serviceType={serviceType} />
                            </StyledCard>
                        </Col>
                        <Col md={12}>
                            <StyledCard>
                                <MemoizedUsageChart serviceType={serviceType} />
                            </StyledCard>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <div style={{ marginTop: '2rem', marginBottom: '1.5rem' }}>
                <SectionTitle>청구 정보</SectionTitle>
            </div>

            <Row className="g-3">
                <Col lg={12}>
                    <MemoizedBillingPanel
                        detail={selectedDetail}
                        yearMonth={selectedYearMonth}
                        monthOptions={monthOptions}
                        onMonthChange={handleMonthChange}
                        isFinalAmount={!!selectedDetail?.finalAmount}
                    />
                </Col>
            </Row>
        </PageContainer>
    );
};

export default BillingDashboard;
